import {
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { SocialReportDetail } from "core/types/social.types";
import { Text } from "components/atoms/text";
import { AgeChart } from "components/molecules/age-chart";
import { CountryChart } from "components/molecules/country-chart";
import { KeyValueInsights } from "components/atoms/key-value-insights";
import { SoloBar } from "components/atoms/solo-bar";
import { grey } from "@mui/material/colors";

interface Props {
  isReportLoading: boolean;
  reportDetail?: SocialReportDetail;
}

const AudiendeContent: FunctionComponent<Props> = ({
  isReportLoading,
  reportDetail,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const topCountriesLanguages = reportDetail?.profileAdvancedInsights
    ?.topCountriesLanguages
    ? reportDetail?.profileAdvancedInsights?.topCountriesLanguages
    : undefined;
  const topGenderAge = reportDetail?.profileAdvancedInsights?.topGenderAge
    ? reportDetail?.profileAdvancedInsights?.topGenderAge
    : undefined;
  const topCities = reportDetail?.profileAdvancedInsights?.topCities
    ? reportDetail?.profileAdvancedInsights?.topCities
    : undefined;

  console.log(topCountriesLanguages?.values.topCountries);

  return (
    <div style={{ overflowX: "auto", width: "100%" }}>
      {isReportLoading ? (
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <CircularProgress color="secondary" size={"100px"} />
        </div>
      ) : (
        reportDetail && (
          <Paper
            sx={{
              borderRadius: "10px",
              paddingY: "20px",
            }}
          >
            <Text fontSize={"18px"} fontWeight={"600"} pb={"15px"}>
              {t("social-reports.audience.title")}
            </Text>
            {reportDetail?.profileAdvancedInsights && (
              <Grid
                container
                pb={"20px"}
                sx={{
                  borderBottom: `0.25px solid ${theme.palette.divider}`,
                }}
              >
                <Grid item container spacing={2} xs={12} sm={8}>
                  <Grid item xs={4}>
                    <KeyValueInsights
                      title={t("social-report-detail.audience.top-country")}
                      content={
                        topCountriesLanguages &&
                        topCountriesLanguages.values.topCountries.value
                          ? t(
                              "social-report-detail.audience." +
                                Object.keys(
                                  topCountriesLanguages.values.topCountries
                                    .value
                                ).reduce((a, b) =>
                                  topCountriesLanguages.values.topCountries
                                    .value[a] >
                                  topCountriesLanguages.values.topCountries
                                    .value[b]
                                    ? a
                                    : b
                                )
                            )
                          : t("empty_states.empty_stats.title")
                      }
                      littleText={
                        topCountriesLanguages &&
                        topCountriesLanguages.values.topCountries.value
                          ? Object.values(
                              topCountriesLanguages.values.topCountries.value
                            )
                              .sort((a, b) => b - a)[0]
                              .toString() + "%"
                          : ""
                      }
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <KeyValueInsights
                      title={t("social-report-detail.audience.top-gender")}
                      content={
                        topGenderAge && topGenderAge.values.topGender.value
                          ? t(
                              "social-report-detail.audience." +
                                topGenderAge.values.topGender.value?.split(
                                  " - "
                                )[0]
                            )
                          : t("empty_states.empty_stats.title")
                      }
                      littleText={
                        topGenderAge
                          ? topGenderAge.values.topGender.value?.split(" - ")[1]
                          : ""
                      }
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <KeyValueInsights
                      title={t("social-report-detail.audience.top-age")}
                      content={
                        topGenderAge && topGenderAge.values.topAge.value
                          ? topGenderAge.values.topAge.value
                              ?.split(" - ")[0]
                              .replace("Range", "")
                          : t("empty_states.empty_stats.title")
                      }
                      littleText={
                        topGenderAge
                          ? topGenderAge.values.topAge.value?.split(" - ")[1]
                          : ""
                      }
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} mt={"20px"}>
              <Grid item xs={12} md={6}>
                <CountryChart
                  title={t("social-report-detail.audience.countries_title")}
                  countryValues={
                    topCountriesLanguages &&
                    topCountriesLanguages?.values?.topCountries?.value
                      ? topCountriesLanguages?.values?.topCountries?.value
                      : {}
                  }
                />
              </Grid>

              <Grid
                item
                sx={{
                  display: { xs: "none", md: "flex" },
                  justifyContent: "center",
                }}
                md={1}
              >
                <div
                  style={{
                    width: "0.5px",
                    background: theme.palette.divider,
                    height: "100%",
                  }}
                ></div>
              </Grid>

              <Grid item xs={12} md={5}>
                <CountryChart
                  title={t("social-report-detail.audience.cities_title")}
                  countryValues={
                    topCities && topCities?.value ? topCities?.value : {}
                  }
                />
              </Grid>
            </Grid>

            <Grid
              container
              display={"flex"}
              justifyContent={"center"}
              mt={"20px"}
              spacing={2}
            >
              <Grid item xs={12}>
                <AgeChart
                  genderAgePercentages={
                    topGenderAge
                      ? topGenderAge?.values?.genderAgePercentages
                      : { values: { F: {}, M: {}, U: {} }, description: "" }
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Text
                  padding={"30px 0px 20px 0px"}
                  fontWeight={700}
                  style={{
                    color: grey[700],
                  }}
                  fontSize={"12px"}
                >
                  {t("social-report-detail.audience.languages_title")}
                </Text>
                {topCountriesLanguages &&
                topCountriesLanguages?.values?.topLanguages?.value ? (
                  Object.entries(
                    topCountriesLanguages?.values?.topLanguages?.value
                  ).map(([key, value]) => (
                    <SoloBar
                      color={theme.palette.primary.main}
                      country={key}
                      percentage={value}
                    />
                  ))
                ) : (
                  <Typography>{t("empty_states.empty_stats.title")}</Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        )
      )}
    </div>
  );
};

export default AudiendeContent;
